import { useChartScrub } from '../../hooks/useChartScrub';
import './ChartStackedZones.css';
import { chartTimeAxisMaximum, formatChartDate, formatChartZoneLabel } from './chartFormatting';
/**
 * Stacked Zones Chart
 * Weekly stacked vertical bars where each segment represents one of the athlete's recorded
 * zones. Bars are clipped to a rounded-rectangle shape so the
 * stack looks like a single pill rather than separate blocks.
 */
import React, { useCallback, useId, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { displayDigits } from '../../utils/displayDigits';
import { formatZoneHours, getZoneDetails, getZoneLegend } from './zoneSeries';
import { svgEl, gridLine, axisLabel } from './svgUtils';
import type { WeekZones } from './types';

const VW = 260;
const MT_DEFAULT = 10;
const MB_DEFAULT = 22;
const Y_AXIS_W = 56;
const LABEL_MUTED = 'var(--fg-2)';
const TIP_COLOR = '#29D4C6';
const Y_INTERVALS = 4;

interface ZoneBar {
  bx: number;
  bw: number;
  cx: number;
  topY: number;
}

interface ZoneLayout {
  vw: number;
  mx: number;
  mr: number;
  mt: number;
  mb: number;
  vh: number;
  cw: number;
  ch: number;
  maxT: number;
  nb: number;
  bw: number;
  gapX: number;
  totals: number[];
  bars: ZoneBar[];
}

interface HoverRow {
  label: string;
  value: string;
  color: string;
}

interface HoverTip {
  left: number;
  top: number;
  date: string;
  rows: HoverRow[];
}

function formatHoverDate(w: WeekZones): string {
  if (w.startDate && w.endDate && w.startDate !== w.endDate) {
    return `${formatChartDate(w.startDate)} – ${formatChartDate(w.endDate)}`;
  }
  return formatChartDate(w.startDate ?? w.week);
}

function zoneRows(w: WeekZones): HoverRow[] {
  const details = getZoneDetails(w);

  return details.map((z) => ({
    label: z.label,
    value: displayDigits(formatZoneHours(z.hrs)),
    color: z.color,
  }));
}

function computeLayout(
  data: WeekZones[],
  mx: number,
  mr: number,
  interactive: boolean,
  vh: number,
  vw: number,
): ZoneLayout | null {
  if (!data.length) return null;
  const mt = interactive ? 4 : MT_DEFAULT;
  const mb = MB_DEFAULT;
  const cw = vw - mx - mr;
  const ch = vh - mt - mb;
  const totals = data.map(w => getZoneDetails(w).reduce((sum, zone) => sum + zone.hrs, 0));
  const maxT = chartTimeAxisMaximum(totals, Y_INTERVALS);
  const nb = data.length;
  const slotW = cw / nb;
  const barRatio = interactive ? 0.86 : 0.62;
  const slotAlign = interactive ? 0.12 : 0.5;
  const bw = Math.max(1, Math.floor(slotW * barRatio));
  const gapX = slotW - bw;

  const bars = data.map((_, i) => {
    const bx = mx + slotW * i + gapX * slotAlign;
    const totalH = (totals[i] / maxT) * ch;
    const topY = mt + ch - totalH;
    return { bx, bw, cx: bx + bw / 2, topY };
  });

  return { vw, mx, mr, mt, mb, vh, cw, ch, maxT, nb, bw, gapX, totals, bars };
}

function clientXToViewBoxX(svg: SVGSVGElement | null, clientX: number): number | null {
  if (!svg) return null;
  const ctm = svg.getScreenCTM();
  if (!ctm) return null;
  const pt = svg.createSVGPoint();
  pt.x = clientX;
  pt.y = 0;
  return pt.matrixTransform(ctm.inverse()).x;
}

function viewBoxPointToWrap(
  svg: SVGSVGElement | null,
  wrap: HTMLDivElement | null,
  x: number,
  y: number,
): { left: number; top: number } | null {
  if (!svg || !wrap) return null;
  const ctm = svg.getScreenCTM();
  if (!ctm) return null;
  const pt = svg.createSVGPoint();
  pt.x = x;
  pt.y = y;
  const screen = pt.matrixTransform(ctm);
  const wrapRect = wrap.getBoundingClientRect();
  return { left: screen.x - wrapRect.left, top: screen.y - wrapRect.top };
}

function barIndexAtX(xVB: number, layout: ZoneLayout): number | null {
  for (let i = 0; i < layout.bars.length; i++) {
    const { bx, bw } = layout.bars[i];
    if (xVB >= bx && xVB <= bx + bw) return i;
  }
  let nearest = 0;
  let best = Math.abs(xVB - layout.bars[0].cx);
  for (let i = 1; i < layout.bars.length; i++) {
    const dist = Math.abs(xVB - layout.bars[i].cx);
    if (dist < best) {
      best = dist;
      nearest = i;
    }
  }
  return nearest;
}

function render(
  svg: SVGSVGElement,
  data: WeekZones[],
  layout: ZoneLayout,
  htmlYLabels: boolean,
  hideXLabels: boolean,
  hoverIdx: number | null,
  instanceId: string,
): void {
  svg.innerHTML = '';
  const { vw, cw, mx: MX, mr: MR, mt, vh, ch, maxT, nb, bw, totals, bars } = layout;
  const labelStep = Math.ceil(nb / Math.max(1, Math.floor(cw / 90)));

  const defs = svgEl('defs');
  const legend = getZoneLegend(data);
  legend.forEach(({ color: c }, i) => {
    const g = svgEl('linearGradient', { class: 'chart-bar-gradient', id: `${instanceId}-zone-${i}`, x1: 0, y1: 0, x2: 0, y2: 1 });
    const s1 = svgEl('stop', { offset: '0%' }); s1.setAttribute('stop-color', c);
    const s2 = svgEl('stop', { offset: '100%' }); s2.setAttribute('stop-color', c); s2.setAttribute('stop-opacity', '0.9');
    g.appendChild(s1); g.appendChild(s2); defs.appendChild(g);
  });

  data.forEach((_, i) => {
    const totalH = (totals[i] / maxT) * ch;
    const bx = bars[i].bx;
    const barTopY = mt + ch - totalH;
    const clip = svgEl('clipPath', { id: `${instanceId}-clip-${i}` });
    clip.appendChild(svgEl('rect', { x: bx, y: barTopY, width: bw, height: totalH, rx: 4 }));
    defs.appendChild(clip);
  });
  svg.appendChild(defs);

  for (let i = 0; i <= Y_INTERVALS; i++) {
    const y = mt + (ch / Y_INTERVALS) * i;
    gridLine(svg, MX, y, vw - MR, y);
    if (!htmlYLabels) {
      axisLabel(svg, MX - 4, y + 3.5, formatZoneHours(maxT * (1 - i / Y_INTERVALS)));
    }
  }

  data.forEach((w, i) => {
    const bx = bars[i].bx;
    const isHover = hoverIdx === i;
    const grp = svgEl('g', {
      'clip-path': `url(#${instanceId}-clip-${i})`,
      opacity: hoverIdx == null || isHover ? 1 : 0.45,
    });
    let curY = mt + ch;
    getZoneDetails(w).forEach(({ hrs: v, label }) => {
      const zi = legend.findIndex(zone => zone.label === label);
      if (!v) return;
      const sh = (v / maxT) * ch;
      const sy = curY - sh;
      grp.appendChild(svgEl('rect', { x: bx, y: sy, width: bw, height: sh, fill: `url(#${instanceId}-zone-${zi})`, stroke: 'var(--training-zone-outline)', 'stroke-width': .8, 'vector-effect': 'non-scaling-stroke' }));
      curY -= sh;
    });
    const title = svgEl('title');
    title.textContent = `${formatHoverDate(w)}: ${zoneRows(w).map(row => `${formatChartZoneLabel(row.label)} ${row.value}`).join(', ')}`;
    grp.appendChild(title);
    svg.appendChild(grp);

    if (isHover) {
      const totalH = (totals[i] / maxT) * ch;
      const barTopY = mt + ch - totalH;
      svg.appendChild(svgEl('rect', {
        x: bx - 0.5,
        y: barTopY - 0.5,
        width: bw + 1,
        height: totalH + 1,
        rx: 4,
        fill: 'none',
        stroke: TIP_COLOR,
        'stroke-width': 1.5,
      }));
    }

    if (!hideXLabels && (i === nb - 1 || (i % labelStep === 0 && nb - 1 - i >= Math.max(1, labelStep - 1)))) {
      axisLabel(svg, bx + bw / 2, vh - 6, formatChartDate(w.startDate ?? w.week), i === 0 ? 'start' : i === nb - 1 ? 'end' : 'middle');
      svg.lastElementChild?.setAttribute('font-size', '11');
    }
  });
}

export interface ChartStackedZonesProps {
  data: WeekZones[];
  /** Edge-to-edge plot — Y ticks as HTML, zero horizontal SVG padding. */
  flush?: boolean;
  /** Show date and per-zone duration on hover or touch. */
  interactive?: boolean;
  height?: number;
  style?: React.CSSProperties;
}

export function ChartStackedZones({
  data,
  interactive = false,
  height = 200,
  style,
}: ChartStackedZonesProps) {
  const { t, i18n } = useTranslation();
  const instanceId = useId().replace(/:/g, '');
  const svgRef = useRef<SVGSVGElement>(null);
  const plotRef = useRef<HTMLDivElement>(null);
  const [plotWidth, setPlotWidth] = useState(VW);
  const [hoverIdx, setHoverIdx] = useState<number | null>(null);
  const [hoverTip, setHoverTip] = useState<HoverTip | null>(null);
  const mx = 4;
  const mr = 4;
  const htmlYLabels = true;
  const hideXLabels = false;
  const layout = useMemo(
    () => computeLayout(data, mx, mr, interactive, height, plotWidth),
    [data, mx, mr, interactive, height, plotWidth],
  );

  // Use CSS pixels for both axes so SVG text keeps its natural proportions.
  useEffect(() => {
    const plot = plotRef.current;
    if (!plot) return;
    const observer = new ResizeObserver(([entry]) => {
      setPlotWidth(Math.max(1, Math.round(entry.contentRect.width)));
      setHoverIdx(null);
      setHoverTip(null);
    });
    observer.observe(plot);
    return () => observer.disconnect();
  }, []);

  useEffect(() => { setHoverIdx(null); setHoverTip(null); }, [data]);

  const yTicks = useMemo(() => {
    if (!layout || !htmlYLabels) return [];
    return Array.from({ length: Y_INTERVALS + 1 }, (_, i) => ({
      value: formatZoneHours(layout.maxT * (1 - i / Y_INTERVALS)),
      topPct: ((layout.mt + (layout.ch / Y_INTERVALS) * i) / layout.vh) * 100,
    }));
  }, [layout, htmlYLabels, i18n.language]);

  useEffect(() => {
    if (svgRef.current && layout) {
      render(svgRef.current, data, layout, htmlYLabels, hideXLabels, interactive ? hoverIdx : null, instanceId);
    }
  }, [data, layout, htmlYLabels, hideXLabels, interactive, hoverIdx, instanceId, i18n.language]);

  const updateHover = useCallback((idx: number | null) => {
    setHoverIdx(idx);
    if (idx == null || !layout) {
      setHoverTip(null);
      return;
    }
    const bar = layout.bars[idx];
    const pos = viewBoxPointToWrap(svgRef.current, plotRef.current, bar.cx, bar.topY);
    if (!pos) {
      setHoverTip(null);
      return;
    }
    setHoverTip({
      left: pos.left,
      top: pos.top - 4,
      date: formatHoverDate(data[idx]),
      rows: zoneRows(data[idx]),
    });
  }, [layout, data]);

  const handleScrub = useCallback((clientX: number) => {
    if (!interactive || !layout) return;
    const xVB = clientXToViewBoxX(svgRef.current, clientX);
    if (xVB == null || xVB < layout.mx || xVB > layout.vw - layout.mr) { updateHover(null); return; }
    updateHover(barIndexAtX(xVB, layout));
  }, [interactive, layout, updateHover]);
  const scrub = useChartScrub(!!interactive, handleScrub, () => updateHover(null));

  return (
    <div className="zone-chart" style={style}>
      <div className="zone-chart__unit">{t('sportDuration.unit')}</div>
    <div
      style={{
        display: 'flex',
        alignItems: 'stretch',
        userSelect: 'none',
        cursor: interactive ? 'crosshair' : undefined,
        direction: 'ltr',
      }}
      {...scrub}
    >
      {htmlYLabels && (
        <div style={{ position: 'relative', width: Y_AXIS_W, flexShrink: 0, height, pointerEvents: 'none' }}>
          {yTicks.map((tick) => (
            <span
              key={tick.value}
              style={{
                position: 'absolute',
                right: 2,
                top: `${tick.topPct}%`,
                transform: 'translateY(-50%)',
                fontSize: 12,
                fontWeight: 600,
                color: LABEL_MUTED,
                lineHeight: 1.7,
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {displayDigits(tick.value)}
            </span>
          ))}
        </div>
      )}

      <div ref={plotRef} style={{ position: 'relative', flex: 1, minWidth: 0 }}>
        {interactive && hoverTip && (
          <div className="zone-chart__tooltip" dir={i18n.dir()}
            style={{
              position: 'absolute',
              left: hoverTip.left,
              top: hoverTip.top,
              transform: 'translate(-50%, -100%)',
              pointerEvents: 'none',
              textAlign: 'left',
              paddingBottom: 4,
            }}
          >
            <div style={{ fontSize: 11, fontWeight: 600, color: LABEL_MUTED, lineHeight: 1.4, marginBottom: 4, textAlign: 'center' }}>
              {hoverTip.date}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {hoverTip.rows.map((row) => (
                <div
                  key={row.label}
                  style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, lineHeight: 1.7, whiteSpace: 'nowrap' }}
                >
                  <span style={{ width: 6, height: 6, borderRadius: 2, background: row.color, flexShrink: 0 }} />
                  <span style={{ color: LABEL_MUTED, fontWeight: 600, maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {formatChartZoneLabel(row.label)}
                  </span>
                  <span style={{ color: 'var(--fg-1)', fontWeight: 700, marginInlineStart: 'auto', paddingInlineStart: 8 }}>
                    {displayDigits(row.value)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
        <svg
          ref={svgRef}
          role="img" aria-label={t('sportDuration.unit')}
          viewBox={`0 0 ${plotWidth} ${height}`}
          preserveAspectRatio="xMidYMid meet"
          style={{ width: '100%', height, display: 'block' }}
        />
      </div>
    </div>
    </div>
  );
}
